import type { FC, PropsWithChildren } from "react";
import { useParams } from "react-router-dom";
import type { SWRConfiguration } from "swr";

import type { GetTrialIdentityByIdResponse } from "src/api/Tmd.dtos";
import TrialIdentityContext from "src/contexts/data/TrialIdentityContext";
import useTrialIdentityById from "src/hooks/data/load/useTrialIdentityById";

/**
 * Configures the trial identity request and the descendants receiving its state.
 */
interface ITrialProviderProps extends PropsWithChildren {
    id?: number;
    configuration?: SWRConfiguration<GetTrialIdentityByIdResponse, GetTrialIdentityByIdResponse>;
}

/**
 * Publishes the trial identity loader state to descendants.
 *
 * Postconditions: Children receive the current loader result, including loading and error state;
 * rendering this provider does not guarantee a completed request. Children remain rendered while
 * loading or on failure. The supplied ID and SWR configuration pass through unchanged.
 */
const TrialIdentityProvider: FC<ITrialProviderProps> = ({ children, id, configuration }) => {
    const context = useTrialIdentityById(id, configuration);

    return (
        <TrialIdentityContext.Provider value={context}>{children}</TrialIdentityContext.Provider>
    );
};

/**
 * Loads trial identity from the matching route parameter for descendants.
 *
 * Preconditions: Render within the router that supplies the route parameters.
 * Postconditions: An absent or empty parameter becomes undefined; otherwise base-10 parseInt
 * supplies the ID, accepting numeric prefixes and producing NaN for unparseable text.
 * Loading and errors remain in context; this wrapper does not gate children or enforce access.
 * Assumptions: The loader handles request state and the server enforces authorization.
 */
export const RouteTrialIdentityProvider: FC<PropsWithChildren> = ({ children }) => {
    const { trialId } = useParams<{ trialId: string }>();
    const id = trialId ? parseInt(trialId, 10) : undefined;

    return <TrialIdentityProvider id={id}>{children}</TrialIdentityProvider>;
};

export default TrialIdentityProvider;
